import React, { useState, useEffect, useCallback } from "react";
import {
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Tooltip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,

} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { investmentService } from "../investments/investmentService";
import { opportunitiesService } from "../opportunities/opportunitiesService";
import { formatCurrency } from "../capital/capitalUtils";

const InvestmentHistory = () => {
    const { t, i18n } = useTranslation();
    const { currentUser } = useAuth();
    const [investments, setInvestments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");

    const statusOptions = [
        { value: "all", label: t("status.all", "All") },
        { value: "active", label: t("status.active", "Active") },
        { value: "pending", label: t("status.pending", "Pending") },
        { value: "completed", label: t("status.completed", "Completed") },
        { value: "cancelled", label: t("status.cancelled", "Cancelled") }
    ];

    const statusTooltips = {
        active: t("status.activeTooltip", "Investment is ongoing and accruing returns."),
        pending: t("status.pendingTooltip", "Investment is awaiting approval or funding."),
        completed: t("status.completedTooltip", "Investment has finished and returns are realized."),
        cancelled: t("status.cancelledTooltip", "Investment was cancelled.")
    };

    const loadInvestmentHistory = useCallback(async () => {
        try {
            setLoading(true);
            const userInvestments = await investmentService.getUserInvestments(currentUser.uid);
      
            // Fetch opportunity details for each investment
            const investmentsWithDetails = await Promise.all(
                userInvestments.map(async (investment) => {
                    try {
                        const opportunity = await opportunitiesService.getOpportunityById(investment.opportunityId);
                        return {
                            ...investment,
                            opportunity
                        };
                    } catch (error) {
                        return {
                            ...investment,
                            opportunity: { title: "Opportunity not found" }
                        };
                    }
                })
            );
      
            setInvestments(investmentsWithDetails);
        } catch (error) {
            // setError("Error loading investment history");
        } finally {
            setLoading(false);
        }
    }, [currentUser?.uid]);

    useEffect(() => {
        if (currentUser) {
            loadInvestmentHistory();
        }
    }, [currentUser, loadInvestmentHistory]);

    const handleViewDetails = (investment) => {
        setSelectedInvestment(investment);
        setDialogOpen(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
        case "completed":
            return "success";
        case "pending":
            return "warning";
        case "cancelled":
            return "error";
        default:
            return "default";
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    // Filter investments by status
    const filteredInvestments = statusFilter === "all" ? investments : investments.filter(inv => inv.status === statusFilter);

    if (loading) {
        return <Typography>Loading investment history...</Typography>;
    }

    return (
        <Paper sx={{ p: 4, borderRadius: 4, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
            <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#155263", borderBottom: "2px solid #DBC078", pb: 1, mb: 3 }}
            >
                {t("investmentHistory", "Investment History")}
            </Typography>

            {/* Add filter dropdown above the table */}
            <FormControl size="small" sx={{ mb: 2, minWidth: 180 }}>
                <InputLabel>{t("status.filter", "Filter by Status")}</InputLabel>
                <Select
                    value={statusFilter}
                    label={t("status.filter", "Filter by Status")}
                    onChange={e => setStatusFilter(e.target.value)}
                >
                    {statusOptions.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {investments.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                        {t("noInvestments", "No investments found.")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t("startInvesting", "Start investing to see your history here.")}
                    </Typography>
                </Box>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: "#155263" }}>
                                    {t("opportunity", "Opportunity")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#155263" }}>
                                    {t("amount", "Amount")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#155263" }}>
                                    {t("date", "Date")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#155263" }}>
                                    {t("status", "Status")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: "#155263" }}>
                                    {t("actions", "Actions")}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredInvestments.map((investment) => (
                                <TableRow key={investment.id}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {investment.opportunity?.title || "Unknown Opportunity"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatCurrency(investment.amount ?? 0, "EUR", i18n.language)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {formatDate(investment.createdAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={statusTooltips[investment.status] || ""} arrow>
                                            <Chip
                                                label={investment.status}
                                                color={getStatusColor(investment.status)}
                                                size="small"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => handleViewDetails(investment)}
                                        >
                                            {t("viewDetails", "View Details")}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Investment Details Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {t("investmentDetails", "Investment Details")}
                </DialogTitle>
                <DialogContent>
                    {selectedInvestment && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("opportunity", "Opportunity")}
                                </Typography>
                                <Typography variant="body1">
                                    {selectedInvestment.opportunity?.title}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("amount", "Amount")}
                                </Typography>
                                <Typography variant="body1">
                                    {formatCurrency(selectedInvestment.amount ?? 0, "EUR", i18n.language)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("date", "Date")}
                                </Typography>
                                <Typography variant="body1">
                                    {formatDate(selectedInvestment.createdAt)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("status", "Status")}
                                </Typography>
                                <Chip
                                    label={selectedInvestment.status}
                                    color={getStatusColor(selectedInvestment.status)}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("description", "Description")}
                                </Typography>
                                <Typography variant="body1">
                                    {selectedInvestment.opportunity?.description || "No description available"}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("expectedReturn", "Expected Return")}
                                </Typography>
                                <Typography variant="body1">
                                    {selectedInvestment.opportunity?.expectedReturn}%
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {t("duration", "Duration")}
                                </Typography>
                                <Typography variant="body1">
                                    {selectedInvestment.opportunity?.duration} {t("months", "months")}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>
                        {t("close", "Close")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default InvestmentHistory; 